import { FormControlProps } from '@material-ui/core/FormControl';
import Snackbar from '@material-ui/core/Snackbar';
import TextField from '@material-ui/core/TextField';
import { Alert } from '@material-ui/lab';
import classNames from 'classnames';
import get from 'lodash/get';
import { ReactElement, useState } from 'react';
import { FieldErrors, FieldValues, Path, UseFormRegister } from 'react-hook-form';

import { ReactComponent as CopyIcon } from '~/ui/assets/images/copy-icon.svg';

import styles from './Textarea.module.scss';

interface IProps<TFieldValues extends FieldValues = FieldValues> {
  register: UseFormRegister<TFieldValues>;
  errors: FieldErrors;
  rows?: number;
  size?: FormControlProps['size'];
  name: string;
  label?: string;
  disabled?: boolean;
  placeholder?: string;
  tabIndex?: number;
  showCopyButton?: boolean;
  onCopy?: () => void;
  readOnly?: boolean;
}

const Textarea = <TFieldValues extends FieldValues = FieldValues>({
  register,
  errors,
  rows = 4,
  size = 'medium',
  name,
  label = '',
  disabled = false,
  placeholder = '',
  tabIndex,
  showCopyButton = false,
  onCopy,
  readOnly = false,
}: IProps<TFieldValues>): ReactElement => {
  const { ref, ...registerProps } = register(name as Path<TFieldValues>);
  const [showSnackbar, setShowSnackbar] = useState(false);

  const error = get(errors, `${name}.message`, '');

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    registerProps.onChange?.(event);
  };

  const handleCopy = () => {
    onCopy?.();
    setShowSnackbar(true);
  };

  return (
    <div className={styles.container}>
      <TextField
        inputProps={{
          tabIndex,
          readOnly,
        }}
        {...registerProps}
        inputRef={ref}
        variant="outlined"
        multiline
        minRows={rows}
        size={size}
        className={classNames(styles.textarea, {
          [styles.readOnly]: readOnly,
        })}
        disabled={disabled}
        placeholder={placeholder}
        label={label}
        InputLabelProps={{ shrink: true }}
        error={Boolean(error)}
        helperText={error.toString()}
        onChange={showCopyButton ? handleTextChange : registerProps.onChange}
      />
      {showCopyButton && (
        <CopyIcon fontSize="small" onClick={handleCopy} className={styles.copyButton} />
      )}
      <Snackbar
        open={showSnackbar}
        autoHideDuration={2000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowSnackbar(false)} severity="success">
          Text copied to clipboard!
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Textarea;
